import { useCallback, useEffect, useMemo, useState } from 'react';
import { useValidateBulkFilterMutation } from '../../../../../../hooks/api/useBulkFilter';
import { useFilterConfigs } from '../../../../../../hooks/useFilterConfigs';
import type { ExpressionType } from '../../../../../../stores/filters';
import {
  type BulkValidationResult,
  EXCEL_EXTENSIONS,
  MAX_BULK_ITEMS,
  MAX_FILE_SIZE_BYTES,
  parseRawInput,
} from '../utils';
import { getFileExtension, isAllowedFile } from '../utils/fileValidation';

interface UseBulkUploadProps {
  initialType?: ExpressionType;
  onUpload: (result: BulkValidationResult, expressionType: ExpressionType) => void;
  filterName: string;
}

export const useBulkUpload = ({ initialType = 'target', onUpload, filterName }: UseBulkUploadProps) => {
  const [rawInput, setRawInput] = useState('');
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [submissionError, setSubmissionError] = useState<string>();
  const [expressionType, setExpressionType] = useState(initialType);

  const { getFilterConfig } = useFilterConfigs({ disableAutoFetch: true });
  const semanticSearch = getFilterConfig(filterName)?.semanticSearch ?? false;

  const { trigger: validateMutation, isLoading } = useValidateBulkFilterMutation();
  const { validItems, invalidLines } = useMemo(() => parseRawInput(rawInput), [rawInput]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: rawInput triggers reset
  useEffect(() => {
    if (submissionError) {
      setSubmissionError(undefined);
    }
  }, [rawInput]);

  const handleFileDrop = useCallback(async (file: File) => {
    setIsProcessingFile(true);
    setSubmissionError(undefined);
    try {
      if (!isAllowedFile(file)) {
        const ext = getFileExtension(file.name);
        setSubmissionError(
          `Unsupported file type${ext ? ` ".${ext}"` : ''}. Please upload a .txt, .csv, .tsv, .xls, or .xlsx file.`,
        );
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        setSubmissionError(`File is too large (${Math.round(file.size / 1024)} KB). Maximum allowed size is 500 KB.`);
        return;
      }

      const isExcel = EXCEL_EXTENSIONS.includes(getFileExtension(file.name));
      const content = isExcel ? await file.arrayBuffer() : await file.text();
      const { validItems: cleanItems } = parseRawInput(content, true);

      setRawInput((prev) => {
        const existing = prev.split('\n').filter(Boolean);
        const merged = Array.from(new Set([...existing, ...cleanItems]));
        return merged.join('\n');
      });
    } catch {
      setSubmissionError('Failed to read file. Please try again.');
    } finally {
      setIsProcessingFile(false);
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isProcessingFile || isLoading) return;
    try {
      const validateResult = await validateMutation({
        filter_name: filterName,
        filter_values: validItems,
        semantic_search: semanticSearch,
      });
      const result: BulkValidationResult = {
        unrecognizedItems: validateResult.unmatched,
        recognizedItems: validateResult.matched,
      };
      onUpload(result, expressionType);
    } catch {
      setSubmissionError('An unexpected error occurred during validation.');
    }
  }, [isProcessingFile, isLoading, onUpload, validItems, expressionType, filterName, semanticSearch, validateMutation]);

  return {
    rawInput,
    setRawInput,
    handleFileDrop,
    handleSubmit,

    expressionType,
    setExpressionType,

    validItems,
    invalidLines,

    isProcessingFile,
    isLoading,
    submissionError,

    itemCount: validItems.length,
    isOverLimit: validItems.length > MAX_BULK_ITEMS,
  };
};
